import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, Mail } from "lucide-react";
import { trackPurchase } from "../lib/metaPixel";

/**
 * Stripe ONLY redirects to /checkout-success after a successful payment
 * (success_url is wired to the post-payment hook). So we always trust this
 * page = a successful payment and immediately show success UI.
 *
 * In the background, we kick off a single status call to nudge our backend
 * to reconcile (grant ticket + send welcome email + fire CK + Meta pixel).
 * If that call fails or is slow, the backend webhook + nightly reconcile +
 * admin "reconcile" button all serve as belt-and-braces — no buyer ever
 * sees a "we couldn't verify your payment" screen again.
 */
export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    if (!sessionId || triggered.current) return;
    triggered.current = true;

    let cancelled = false;
    let elapsed = 0;
    const POLL_MS = 3000;
    const TOTAL_MS = 90_000; // try for 90s, then quietly stop — backend will reconcile later

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // Fire Meta Pixel Purchase (deduped per session inside the helper).
          // Use the backend-issued purchase_event_id so this Pixel event
          // dedupes with the server-side CAPI Purchase event.
          const valueGBP = (data.amount_total || 0) / 100;
          trackPurchase({
            value: valueGBP,
            currency: (data.currency || "gbp").toUpperCase(),
            content_ids: data.package_id ? [data.package_id] : undefined,
            content_name: data.package_id === "virtual_concession"
              ? "Nurses & Trainees"
              : data.package_id === "virtual_full"
              ? "Full Access"
              : undefined,
            transaction_id: sessionId,
            event_id: data.purchase_event_id,
          });
          setEmailConfirmed(true);
          try { await refresh(); } catch (e) { /* harmless if not signed in */ }
          return;
        }
      } catch (e) {
        // ignore — silent retry. The success UI is already showing.
      }
      elapsed += POLL_MS;
      if (elapsed < TOTAL_MS && !cancelled) {
        setTimeout(tick, POLL_MS);
      }
    };
    tick();

    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-12">
      <div className="max-w-lg w-full text-center" data-testid="checkout-success-page">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#28BBE3]/15 flex items-center justify-center">
          <CheckCircle2 className="text-[#28BBE3]" size={32} strokeWidth={1.8} />
        </div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Payment received</div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-slate-900">
          Welcome to <span className="italic font-normal text-[#28BBE3]">DFTB26.</span>
        </h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Your virtual ticket is confirmed and your login details are on the way to your inbox.
          The live stream opens 22 June 2026 inside your portal - yours to revisit for 12 months after.
        </p>

        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-xs text-slate-600" data-testid="email-status-line">
          <Mail size={12} className="text-[#28BBE3]" />
          {emailConfirmed
            ? <span>Login email sent — check your inbox now.</span>
            : <span>Sending your login email…</span>}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {user ? (
            <Button onClick={() => navigate("/portal")} className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white px-6 py-6" data-testid="success-portal-btn">
              Open my portal
            </Button>
          ) : (
            <>
              <Button onClick={() => navigate("/login")} className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white px-6 py-6" data-testid="success-login-btn">
                Log in with my new password
              </Button>
              <Link to="/" className="text-sm text-slate-500 hover:text-[#28BBE3] underline-offset-4 hover:underline">
                Back to home
              </Link>
            </>
          )}
        </div>

        <p className="mt-8 text-xs text-slate-400" data-testid="success-support-line">
          No email after 10 minutes? Check spam, then email{" "}
          <a href="mailto:registration@dontforgetthebubbles.com" className="text-[#28BBE3] hover:underline">
            registration@dontforgetthebubbles.com
          </a>
        </p>
      </div>
    </div>
  );
}
